// DAS-2107 / DAS-2139 / DAS-2149: "Your Portfolio" modal with dynamic column selection + weight %

import React, { useState, useMemo } from 'react';
import {
  Box, Typography,
  Table as MuiTable, TableHead, TableBody, TableRow, TableCell,
  TableContainer, TableSortLabel, Paper, TextField, InputAdornment, Chip,
  Menu, MenuItem, Checkbox, ListItemText, IconButton, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ClickableTickerChip from './ClickableTickerChip';

const fmtCurrency = (v) => {
  if (v == null || isNaN(v)) return '—';
  return `€${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtPercent = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
};

const fmtWeight = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return `${n.toFixed(2)}%`;
};

const fmtText = (v) => v || '—';

const fmtDate = (v) => (!v ? '—' : String(v));

const FIXED_COLUMNS = [
  { id: 'Ticker', label: 'Ticker', field: 'Ticker', align: 'left', sortable: true },
  { id: 'Company', label: 'Company', field: 'Company', align: 'left', sortable: true },
  { id: 'Position', label: 'Position', field: 'Position', align: 'right', sortable: true },
  { id: 'Value_eur', label: 'Value (EUR)', field: 'Value_eur', align: 'right', sortable: true },
  { id: '_weight_pct', label: 'Weight %', field: '_weight_pct', align: 'right', sortable: true },
];

const OPTIONAL_COLUMNS = [
  { id: 'Close', label: 'Last Close', field: 'Close', align: 'right', format: fmtCurrency, sortable: true },
  { id: 'unrealized_pnl', label: 'Unrealized P/L', field: 'unrealized_prof_loss_Eur_perc', align: 'right', format: fmtPercent, sortable: true, colorize: true },
  { id: 'realized_pnl', label: 'Realized P/L', field: 'realized_results_current_positions_performance', align: 'right', format: fmtPercent, sortable: true, colorize: true },
  { id: 'perf_since_tx', label: 'Perf. Since Last Tx', field: 'performance_since_last_tx', align: 'right', format: fmtPercent, sortable: true, colorize: true },
  { id: 'benchmark_perf', label: 'Benchmark Perf.', field: 'benchmark_perf_since_last_tx', align: 'right', format: fmtPercent, sortable: true, colorize: true },
  { id: 'Category', label: 'Category', field: 'Category', align: 'left', format: fmtText, sortable: true },
  { id: 'currency', label: 'Currency', field: 'currency', align: 'left', format: fmtText, sortable: false },
  { id: 'asset_type', label: 'Asset Type', field: 'asset_type', align: 'left', format: fmtText, sortable: false },
  { id: 'latest_tx_date', label: 'Last Tx Date', field: 'latest_tx_date', align: 'left', format: fmtDate, sortable: true },
  { id: 'exposure_pct', label: 'Exposure %', field: 'exposure_pct', align: 'right', format: fmtPercent, sortable: true },
  { id: 'purchase_cost', label: 'Purchase Cost', field: 'purchase_cost', align: 'right', format: fmtCurrency, sortable: true },
  { id: 'latest_price', label: 'Latest Price', field: 'latest_price', align: 'right', format: fmtCurrency, sortable: true },
  { id: 'code', label: 'Code (ISIN/CUSIP)', field: 'code', align: 'left', format: fmtText, sortable: false },
];

const PAModal_Portfolio = ({ data, source = 'portfolio' }) => {
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('Value_eur');
  const [order, setOrder] = useState('desc');
  const [selectedColumns, setSelectedColumns] = useState(new Set());
  const [menuAnchor, setMenuAnchor] = useState(null);

  const totalPortfolioValue = useMemo(() => {
    if (!data?.length) return 0;
    return data.reduce((sum, r) => sum + (parseFloat(r.Value_eur) || 0), 0);
  }, [data]);

  const availableColumns = useMemo(() => {
    if (!data?.length) return [];
    return OPTIONAL_COLUMNS.filter(col =>
      data.some(row => {
        const v = row[col.field];
        return v != null && v !== '';
      })
    );
  }, [data]);

  const unselectedCount = useMemo(
    () => availableColumns.filter(c => !selectedColumns.has(c.id)).length,
    [availableColumns, selectedColumns]
  );

  const activeOptionalCols = useMemo(
    () => OPTIONAL_COLUMNS.filter(
      c => selectedColumns.has(c.id) && availableColumns.some(a => a.id === c.id)
    ),
    [selectedColumns, availableColumns]
  );

  const filtered = useMemo(() => {
    let rows = data || [];
    if (search) {
      const lower = search.toLowerCase();
      rows = rows.filter(r =>
        (r.Ticker || '').toLowerCase().includes(lower) ||
        (r.Company || '').toLowerCase().includes(lower)
      );
    }
    const sortField = orderBy === '_weight_pct' ? 'Value_eur' : orderBy;
    return [...rows].sort((a, b) => {
      const av = a[sortField];
      const bv = b[sortField];
      const an = parseFloat(av);
      const bn = parseFloat(bv);
      if (!isNaN(an) && !isNaN(bn)) return order === 'asc' ? an - bn : bn - an;
      const as = String(av || '');
      const bs = String(bv || '');
      return order === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
  }, [data, search, orderBy, order]);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(field);
      setOrder('desc');
    }
  };

  const toggleColumn = (colId) => {
    setSelectedColumns(prev => {
      const next = new Set(prev);
      next.has(colId) ? next.delete(colId) : next.add(colId);
      return next;
    });
  };

  const removeColumn = (colId) => {
    setSelectedColumns(prev => {
      const next = new Set(prev);
      next.delete(colId);
      return next;
    });
  };

  const totalValue = filtered.reduce((sum, r) => sum + (parseFloat(r.Value_eur) || 0), 0);

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} gap={1.5} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Search ticker or company..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Box display="flex" alignItems="center" gap={1.5}>
          {availableColumns.length > 0 && (
            <>
              <Chip
                icon={<AddIcon />}
                label={
                  unselectedCount > 0
                    ? `${unselectedCount} other data group${unselectedCount !== 1 ? 's' : ''} available`
                    : 'Manage columns'
                }
                variant="outlined"
                color="primary"
                size="small"
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                sx={{ cursor: 'pointer' }}
              />
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                PaperProps={{ sx: { maxHeight: 360, minWidth: 240 } }}
              >
                {availableColumns.map(col => (
                  <MenuItem key={col.id} dense onClick={() => toggleColumn(col.id)}>
                    <Checkbox
                      checked={selectedColumns.has(col.id)}
                      size="small"
                      sx={{ p: 0.5, mr: 1 }}
                    />
                    <ListItemText primary={col.label} />
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {filtered.length} of {(data || []).length} positions
          </Typography>
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '55vh' }}>
        <MuiTable size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {FIXED_COLUMNS.map(col => (
                <TableCell key={col.id} align={col.align} sx={{ fontWeight: 600 }}>
                  {col.sortable ? (
                    <TableSortLabel
                      active={orderBy === col.field}
                      direction={orderBy === col.field ? order : 'asc'}
                      onClick={() => handleSort(col.field)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : col.label}
                </TableCell>
              ))}
              {activeOptionalCols.map(col => (
                <TableCell key={col.id} align={col.align} sx={{ fontWeight: 600 }}>
                  <Box display="flex" alignItems="center" gap={0.5} sx={{ whiteSpace: 'nowrap' }}>
                    {col.sortable ? (
                      <TableSortLabel
                        active={orderBy === col.field}
                        direction={orderBy === col.field ? order : 'asc'}
                        onClick={() => handleSort(col.field)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      <span>{col.label}</span>
                    )}
                    <Tooltip title="Remove column">
                      <IconButton
                        size="small"
                        onClick={() => removeColumn(col.id)}
                        sx={{ p: 0.25, opacity: 0.4, '&:hover': { opacity: 1 } }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((row, idx) => (
              <TableRow key={row.id || `${row.Ticker}-${idx}`} hover>
                <TableCell>
                  <ClickableTickerChip ticker={row.Ticker} source={source} />
                </TableCell>
                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.Company || '—'}
                </TableCell>
                <TableCell align="right">
                  {row.Position != null ? Number(row.Position).toLocaleString() : '—'}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 500 }}>
                  {fmtCurrency(row.Value_eur)}
                </TableCell>
                <TableCell align="right">
                  {totalPortfolioValue > 0
                    ? fmtWeight((parseFloat(row.Value_eur) || 0) / totalPortfolioValue * 100)
                    : '—'}
                </TableCell>
                {activeOptionalCols.map(col => {
                  const val = row[col.field];
                  const formatted = col.format(val);
                  const sx = {};
                  if (col.colorize) {
                    const n = parseFloat(val);
                    if (!isNaN(n)) {
                      sx.color = n >= 0 ? '#2e7d32' : '#c62828';
                      sx.fontWeight = 600;
                    }
                  }
                  return (
                    <TableCell key={col.id} align={col.align} sx={sx}>
                      {formatted}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            {filtered.length > 0 && (
              <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' } }}>
                <TableCell colSpan={3} align="right">Total</TableCell>
                <TableCell align="right">{fmtCurrency(totalValue)}</TableCell>
                <TableCell align="right">
                  {totalPortfolioValue > 0
                    ? fmtWeight(totalValue / totalPortfolioValue * 100)
                    : '—'}
                </TableCell>
                {activeOptionalCols.map(col => (
                  <TableCell key={col.id} />
                ))}
              </TableRow>
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </Box>
  );
};

export default PAModal_Portfolio;
